import classnames from 'classnames';
import Hammer from 'hammerjs';
import { map, get } from 'lodash';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';

import AspectRatio from 'components/AspectRatio';
import Chevron, { DIRECTIONS, SIZES } from 'components/Chevron';
import IconButton from 'components/IconButton';
import ImageV2 from 'components/ImageV2';
import Theme from 'styles/theme';
import I18n from 'utils/I18n';

class MediaGallery extends PureComponent {
  constructor(props) {
    super(props);

    this.stageRef = React.createRef();
  }

  componentDidMount() {
    this.setupGestures();
    this.setScrollLeft();
  }

  componentDidUpdate() {
    this.setScrollLeft();
  }

  componentWillUnmount() {
    this.teardownGestures();
  }

  onNext() {
    this.props.onIndexChange(this.nextIndex);
  }

  onPrevious() {
    this.props.onIndexChange(this.previousIndex);
  }

  setScrollLeft() {
    if (this.stageRef.current) {
      this.stageRef.current.scrollLeft = this.stageWidth * this.props.activeIndex;
    }
  }

  get nextIndex() {
    return this.props.activeIndex === this.props.media.length - 1 ? 0 : this.props.activeIndex + 1;
  }

  get previousIndex() {
    return this.props.activeIndex === 0 ? this.props.media.length - 1 : this.props.activeIndex - 1;
  }

  get stageWidth() {
    return get(this.stageRef, ['current', 'clientWidth']);
  }

  setupGestures() {
    this.$gestures = new Hammer.Manager(this.stageRef.current);
    this.$gestures.add(new Hammer.Swipe({ direction: Hammer.DIRECTION_HORIZONTAL }));
    this.$gestures.on('swipe', event => this.handleSwipe(event));
  }

  teardownGestures() {
    this.$gestures.destroy();
  }

  handleSwipe(event) {
    if (event.direction === Hammer.DIRECTION_LEFT) {
      this.onNext();
    } else if (event.direction === Hammer.DIRECTION_RIGHT) {
      this.onPrevious();
    }
  }

  render() {
    const { activeIndex, media, isResponsive, className, ...otherProps } = this.props;
    const hasCarousel = get(media, 'length') > 1;
    const classes = classnames(
      'MediaGallery',
      { 'MediaGallery--is-responsive': isResponsive },
      className
    );

    return (
      <div className={classes} {...otherProps}>
        <div className="MediaGallery__stage" ref={this.stageRef}>
          <div className="MediaGallery__list">
            {map(media, (mediaObject, index) => {
              const containerClassnames = classnames('MediaGallery__image-container', {
                'MediaGallery__image-container--active': index === activeIndex || !hasCarousel,
              });
              const { width, height } = mediaObject.file.details.image;

              return (
                <div className={containerClassnames} key={`image-${index}`}>
                  {isResponsive ? (
                    <ImageV2
                      alt={mediaObject.title}
                      src={mediaObject.file.url}
                      width={width}
                      height={height}
                      className="MediaGallery__image"
                      coverParentNode
                    />
                  ) : (
                    <AspectRatio width={width} height={height}>
                      <ImageV2
                        alt={mediaObject.title}
                        src={mediaObject.file.url}
                        width={width}
                        height={height}
                        className="MediaGallery__image"
                        coverParentNode
                      />
                    </AspectRatio>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {hasCarousel && (
          <>
            <div className="MediaGallery__control MediaGallery__control--previous">
              <IconButton
                onClick={() => this.onPrevious()}
                aria-label={I18n.localize('carousel.viewPreviousSlide')}
              >
                <Chevron
                  fillColor="colors-white"
                  direction={DIRECTIONS.LEFT}
                  size={SIZES.EXTRA_LARGE}
                />
              </IconButton>
            </div>

            <div className="MediaGallery__control MediaGallery__control--next">
              <IconButton
                onClick={() => this.onNext()}
                aria-label={I18n.localize('carousel.viewNextSlide')}
              >
                <Chevron
                  fillColor="colors-white"
                  direction={DIRECTIONS.RIGHT}
                  size={SIZES.EXTRA_LARGE}
                />
              </IconButton>
            </div>

            <div className="MediaGallery__dots">
              {map(media, (_, index) => {
                const dotClasses = classnames('MediaGallery__dot', {
                  'MediaGallery__dot--active': index === activeIndex,
                });

                return <i className={dotClasses} key={`dot-${index}`} />;
              })}
            </div>
          </>
        )}

        <style jsx>
          {`
            .MediaGallery {
              position: relative;
              display: flex;
            }

            .MediaGallery__stage {
              position: relative;
              width: 100%;
              height: 100%;
              overflow: hidden;
            }

            .MediaGallery__list {
              display: flex;
              flex-wrap: nowrap;
              white-space: nowrap;
            }

            .MediaGallery--is-responsive .MediaGallery__list {
              height: 100%;
            }

            .MediaGallery__image-container {
              position: relative;
              min-width: 100%;
              min-height: 100%;
              opacity: 0;
            }

            .MediaGallery__image-container--active {
              opacity: 1;
            }

            .MediaGallery__control {
              position: absolute;
              top: 50%;
              transform: translateY(-50%);
              padding: ${Theme.get('spacing') * 4}px;
              touch-action: manipulation;
            }

            .MediaGallery__control--next {
              right: 0;
            }

            .MediaGallery__control--previous {
              left: 0;
            }

            .MediaGallery__dots {
              position: absolute;
              display: flex;
              width: 100%;
              justify-content: center;
              align-items: center;
              bottom: ${Theme.get('spacing') * 4}px;
            }

            .MediaGallery__dot {
              display: block;
              width: ${Theme.get('spacing')}px;
              height: ${Theme.get('spacing')}px;
              margin: 0 ${Theme.get('spacing')}px;
              background-color: ${Theme.get('colors-white')};
              border-radius: 100%;
            }

            .MediaGallery__dot--active {
              width: ${Theme.get('spacing') * 2}px;
              height: ${Theme.get('spacing') * 2}px;
            }

            .MediaGallery__image {
              background-repeat: no-repeat;
              background-position: center center;
              background-size: cover;
            }

            .MediaGallery--is-responsive .MediaGallery__image {
              object-fit: cover;
              height: 100%;
            }
          `}
        </style>
      </div>
    );
  }
}

MediaGallery.propTypes = {
  media: PropTypes.arrayOf(
    PropTypes.shape({
      file: PropTypes.shape({
        cloudinaryPublicId: PropTypes.string,
        details: PropTypes.shape({
          image: PropTypes.shape({
            height: PropTypes.number,
            width: PropTypes.number,
          }),
        }),
      }),
    })
  ).isRequired,
  isResponsive: PropTypes.bool,
  className: PropTypes.string,
  onIndexChange: PropTypes.func,
  activeIndex: PropTypes.number,
};

MediaGallery.defaultProps = {
  className: '',
  isResponsive: false,
  onIndexChange: () => {},
  activeIndex: 0,
};

export default MediaGallery;
